import { map } from 'lodash';
import { TechnologyTypeCode } from '../technologyTypeCode';
import { UnitExtraPartFormModel } from '../quote-or-design-form/units/unitExtraPartFormModel';
import { assertNotNull } from '../../../helpers/nullHelpers';
import { assertIsNumberOrNull } from '../../../helpers/numberHelpers';
import { getPipelineMapWaterVolume } from '../../pdf/helpers/manifoldHelpers';
import { CreateUpdateHeatPumpDto, mapHeatPumpFormModelToDto } from '../heat-pump/createUpdateHeatPumpDto';
import {
  CreateUpdateManifoldDto,
  DesignCreateUpdateManifoldDto,
  mapManifoldFormModelToDesignDto,
  mapManifoldFormModelToQuoteDto,
  QuoteCreateUpdateManifoldDto,
} from '../manifolds/createUpdateManifoldDto';
import { PipeResponse } from '../pipes/pipeResponse';
import { ManifoldFormModel } from '../quote-or-design-form/manifolds/manifoldFormModel';
import {
  PumpLocationCode,
  PumpSelectionTypeCode,
  UnitHeatSourceTypeCode,
} from '../quote-or-design-form/quoteOrDesignFormData';
import {
  unitCanHaveHeatLossInformation,
  UnitFormModel,
  unitHasHeatPump,
  unitHasUnderfloorHeating,
} from '../quote-or-design-form/units/unitFormModel';
import { CreateUpdateRoomDto } from '../rooms/createUpdateRoomDto';
import { CreateUpdateSolarDto, mapSolarFormModelToDto } from '../solar/createUpdateSolarDto';
import {
  CreateUpdateUnitHeatLossDetailsDto,
  mapUnitHeatLossDetailsFormModelToDto,
} from './heatLoss/createUpdateUnitHeatLossDto';

export type CreateUpdateUnitExtraPartDto = {
  partId: number;
  technologyTypeCode: TechnologyTypeCode;
  quantity: number;
};

const mapExtraPartsToDto = (
  extraParts: Array<UnitExtraPartFormModel>,
  technologyTypeCode: TechnologyTypeCode,
): Array<CreateUpdateUnitExtraPartDto> =>
  extraParts.map(({ partId, quantity }) => ({
    partId: partId,
    technologyTypeCode,
    quantity: Number(quantity),
  }));

export type QuoteCreateUpdateUnitDto = CreateUpdateUnitDto<QuoteCreateUpdateManifoldDto>;

export type DesignCreateUpdateUnitDto = CreateUpdateUnitDto<DesignCreateUpdateManifoldDto> & {
  heatLossDetails: CreateUpdateUnitHeatLossDetailsDto | null;
};

type CreateUpdateUnitDto<TManifoldDto> = {
  unitId: number | null;
  name: string;
  displayIndex: number;
  quantity: number;
  numberOfBedrooms: number | null;
  numberOfBathrooms: number | null;
  cylinderVolumeOption: string | null;
  hasPipelineMap: boolean;
  pipelineMapJson: string;
  pipelineMapWaterVolumeInLitres: number | null;
  waterBufferSizeInLitres: number | null;
  boilerCapacityInKilowatts: number | null;
  pumpSelectionTypeCode: PumpSelectionTypeCode;
  pumpLocationCode: PumpLocationCode;
  pumpPartIds: Array<number>;
  extraParts: Array<CreateUpdateUnitExtraPartDto>;
  heatSourceTypeCode: UnitHeatSourceTypeCode | null;
  heatPump: CreateUpdateHeatPumpDto | null;
  solar: CreateUpdateSolarDto | null;
  manifolds: Array<TManifoldDto>;
};

export const mapUnitFormModelToQuoteDto = (formModel: UnitFormModel, unitIndex: number): QuoteCreateUpdateUnitDto => ({
  ...mapUnitFormModelToDto(formModel, unitIndex, [], mapManifoldFormModelToQuoteDto),
  manifolds: map(formModel.manifolds, (manifold, manifoldIndex) =>
    mapManifoldFormModelToQuoteDto(manifold, manifoldIndex, formModel.pumpLocation),
  ),
});

export const mapUnitFormModelToDesignDto = (
  formModel: UnitFormModel,
  unitIndex: number,
  pipes: Array<PipeResponse>,
): DesignCreateUpdateUnitDto => ({
  ...mapUnitFormModelToDto(formModel, unitIndex, pipes, mapManifoldFormModelToDesignDto),
  heatLossDetails:
    unitCanHaveHeatLossInformation(formModel) && formModel.heatLossDetails != null
      ? mapUnitHeatLossDetailsFormModelToDto(assertNotNull(formModel.heatLossDetails))
      : null,
});

const mapUnitFormModelToDto = <
  TManifoldDto extends CreateUpdateManifoldDto<TRoomDto>,
  TRoomDto extends CreateUpdateRoomDto
>(
  formModel: UnitFormModel,
  unitIndex: number,
  pipes: Array<PipeResponse>,
  mapManifoldFormModelToDto: (
    manifoldFormModel: ManifoldFormModel,
    manifoldIndex: number,
    pumpLocation: PumpLocationCode,
  ) => TManifoldDto,
): CreateUpdateUnitDto<TManifoldDto> => {
  return {
    unitId: formModel.unitId,
    name: formModel.name,
    displayIndex: unitIndex,
    quantity: Number(formModel.quantity),
    numberOfBedrooms: assertIsNumberOrNull(formModel.numberOfBedrooms),
    numberOfBathrooms: assertIsNumberOrNull(formModel.numberOfBathrooms),
    cylinderVolumeOption: formModel.cylinderVolumeOption,
    hasPipelineMap: formModel.hasPipelineMap ?? false,
    pipelineMapJson: formModel.pipelineMapJson ?? '',
    waterBufferSizeInLitres: formModel.waterBufferSizeInLitres ?? null,
    boilerCapacityInKilowatts: formModel.boilerCapacityInKilowatts ?? null,
    pipelineMapWaterVolumeInLitres: getPipelineMapWaterVolume(formModel.pipelineMapJson, pipes),
    pumpSelectionTypeCode: formModel.pumpSelectionType,
    pumpLocationCode: formModel.pumpLocation,
    pumpPartIds: formModel.pumps.filter(pump => pump != null).map(pump => assertNotNull(pump).partId),
    extraParts: [
      ...mapExtraPartsToDto(formModel.underfloorHeatingExtraParts, 'UnderfloorHeating'),
      ...mapExtraPartsToDto(formModel.heatPumpExtraParts, 'HeatPump'),
    ],
    heatSourceTypeCode:
      unitHasUnderfloorHeating(formModel) || unitHasHeatPump(formModel) ? formModel.heatSourceTypeCode : null,
    heatPump: formModel.heatPump == null ? null : mapHeatPumpFormModelToDto(formModel.heatPump),
    solar: formModel.solar == null ? null : mapSolarFormModelToDto(formModel.solar),
    manifolds: map(formModel.manifolds, (manifold, manifoldIndex) =>
      mapManifoldFormModelToDto(manifold, manifoldIndex, formModel.pumpLocation),
    ),
  };
};
